import { useEffect, useMemo, useState } from 'react';
import { Button, Chip, Input, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import { Brain as MemoryIcon } from 'lucide-react';
import { memoryModuleService } from '../../services/memoryModuleService';
import { sessionService } from '../../services/sessionService';
import { useNotificationStore } from '../../store/notificationStore';
import { SessionStatus, type AgentSessionDto } from '../../types/session';
import type { AgentMemoryDefinitionDto } from '../../types/memoryModule';

interface SessionMemoryScopeProps {
  session: AgentSessionDto;
  /** Called after the scope was saved so the parent can reload the session. */
  onSaved?: () => void;
}

/**
 * Declared memory scope for one session (Memory spec 01 §2.5 caller payload). Shows the agent's
 * AMD slot dimensions as editable fields (UserId is automatic — never an input) and persists the
 * values on the session, where both chat injection and the Complete/idle enqueue read them.
 * Renders nothing when the agent has no memory definition (§3.6 pre-cutover).
 */
export const SessionMemoryScope = ({ session, onSaved }: SessionMemoryScopeProps) => {
  const { showNotification } = useNotificationStore();
  const [definition, setDefinition] = useState<AgentMemoryDefinitionDto | null>(null);
  const [draftOnlyName, setDraftOnlyName] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [savedValues, setSavedValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void memoryModuleService
      .listDefinitions()
      .then((defs) => {
        if (cancelled) return;
        const forAgent = defs.filter(
          (d) => d.agentKey.toLowerCase() === session.agentDefinitionId.toLowerCase()
        );
        // Runtime enqueue/inject only resolve IsCurrent AMDs — never fall back to a Draft, or the
        // session page looks configured while Complete silently returns Ignored.
        const current = forAgent.find((d) => d.isCurrent) ?? null;
        setDefinition(current);
        setDraftOnlyName(
          current ? null : forAgent.find((d) => !d.isCurrent)?.displayName ?? null
        );
      })
      .catch(() => {
        if (!cancelled) {
          setDefinition(null);
          setDraftOnlyName(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [session.agentDefinitionId]);

  useEffect(() => {
    let parsed: Record<string, string> = {};
    if (session.memoryDeclaredScopeJson) {
      try {
        parsed = JSON.parse(session.memoryDeclaredScopeJson) as Record<string, string>;
      } catch {
        parsed = {};
      }
    }
    setValues(parsed);
    setSavedValues(parsed);
  }, [session.memoryDeclaredScopeJson]);

  const editableSlots = useMemo(
    () =>
      (definition?.scopeSlots ?? []).filter(
        (s) => s.dimensionKey && s.dimensionKey !== 'UserId'
      ),
    [definition]
  );

  const dirty = useMemo(
    () =>
      editableSlots.some(
        (s) => (values[s.dimensionKey!] ?? '') !== (savedValues[s.dimensionKey!] ?? '')
      ),
    [editableSlots, values, savedValues]
  );

  if ((!definition || editableSlots.length === 0) && !draftOnlyName) {
    return null;
  }

  if (!definition || editableSlots.length === 0) {
    return (
      // Left behind by the MUI -> @coordio/ui migration: Box/Typography were never imported here,
      // so this branch could not compile. Rebuilt with the same Tailwind idiom the rest of this
      // component already uses (see the main return below).
      <div className="flex items-center gap-2 border-b border-border bg-muted px-6 py-2.5">
        <MemoryIcon size={18} className="text-amber-500" />
        <span className="text-xs text-muted-foreground">
          Memory definition &quot;{draftOnlyName}&quot; is Draft — activate it under Memory Definitions
          before scope, extraction, or injection will run.
        </span>
      </div>
    );
  }

  const editable =
    session.status === SessionStatus.Active || session.status === SessionStatus.Paused;

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: Record<string, string> = {};
      for (const slot of editableSlots) {
        const value = (values[slot.dimensionKey!] ?? '').trim();
        if (value) payload[slot.dimensionKey!] = value;
      }
      await sessionService.updateMemoryScope(session.id, payload);
      setSavedValues(payload);
      showNotification('Memory scope saved', 'success');
      onSaved?.();
    } catch (err: any) {
      showNotification(err.response?.data?.message || err.message || 'Failed to save memory scope', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="flex flex-wrap items-end gap-x-3 gap-y-2 border-b border-border bg-muted px-6 py-2.5"
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1.5 pb-2">
              <MemoryIcon size={18} className="text-muted-foreground" />
              <span className="text-xs font-semibold text-muted-foreground">
                Memory scope
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent>{`Memory scope for definition "${definition.displayName}". Values partition what this session's memories can recall.`}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {editableSlots.map((slot) => (
        <div key={slot.dimensionKey} className="w-[180px]">
          <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">
            {slot.required ? `${slot.dimensionKey} *` : slot.dimensionKey}
          </label>
          <Input
            value={values[slot.dimensionKey!] ?? ''}
            onChange={(e) =>
              setValues((prev) => ({ ...prev, [slot.dimensionKey!]: e.target.value }))
            }
            disabled={!editable || saving}
          />
        </div>
      ))}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Chip
                label="UserId: automatic"
                variant="outlined"
                color="default"
              />
            </span>
          </TooltipTrigger>
          <TooltipContent>UserId comes from your login — it is never typed in.</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {editable && (
        <Button
          size="sm"
          variant="outline"
          onClick={handleSave}
          disabled={!dirty || saving}
          className="h-[38px]"
        >
          {saving ? 'Saving…' : 'Save scope'}
        </Button>
      )}
    </div>
  );
};
